/*
  Routine builder — storefront behaviour.

  Reads everything it needs from the markup rendered by sections/routine-builder.liquid
  and blocks/_routine-step.liquid: steps, cards, variants, tier settings and labels.
  Posts to /cart/add.js with two hidden line-item properties per item:
    _routine_id   one random id shared by the whole routine
    _routine_step the step key (cleanse, moisturise, protect, boost)
  Prices shown are estimates; the real discount is applied by Shopify at checkout.
*/
(function () {
  'use strict';

  class RoutineBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      const root = this.closest('.routine-builder');
      this.config = {
        tier1Min: Number(root.dataset.tier1Min || 3),
        tier1Pct: Number(root.dataset.tier1Pct || 10),
        tier2Min: Number(root.dataset.tier2Min || 4),
        tier2Pct: Number(root.dataset.tier2Pct || 15),
        moneyFormat: root.dataset.moneyFormat || 'Rs. {{amount}}',
        afterAdd: root.dataset.afterAdd || 'cart',
        cartUrl: root.dataset.cartUrl || '/cart',
        addUrl: root.dataset.cartAddUrl || '/cart/add.js',
        msgMissing: root.dataset.msgMissing || 'Add a {step} to unlock {percent}%.',
        msgNext: root.dataset.msgNext || '{current}% unlocked. Add a {step} to unlock {percent}%.',
        msgMax: root.dataset.msgMax || '{percent}% unlocked.',
        msgAdding: root.dataset.msgAdding || 'Adding…',
        msgAdded: root.dataset.msgAdded || 'Added to your cart.',
        msgError: root.dataset.msgError || "Couldn't add the routine. Try again.",
        viewCartLabel: root.dataset.viewCartLabel || 'View cart',
      };

      this.steps = Array.from(this.querySelectorAll('.routine-builder__step')).map((el, index) => ({
        el,
        id: el.dataset.step,
        key: el.dataset.stepKey,
        title: el.dataset.stepTitle,
        required: el.dataset.required === 'true',
        index,
        emptyLabel: el.dataset.emptySlotLabel || 'Choose one',
        selection: null,
      }));

      this.rail = this.querySelector('[data-routine-rail]');
      this.slots = this.querySelector('[data-routine-slots]');
      this.summary = this.querySelector('[data-routine-summary]');
      this.tierMessage = this.querySelector('[data-tier-message]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.compareEl = this.querySelector('[data-summary-compare]');
      this.totalEl = this.querySelector('[data-summary-total]');
      this.submit = this.querySelector('[data-add-routine]');
      this.error = this.querySelector('[data-routine-error]');
      this.submitLabel = this.submit ? this.submit.textContent.trim() : 'Add routine to cart';

      this.buildRail();
      this.buildSlots();
      this.bind();
      this.render();
    }

    /* ---------- build ---------- */

    buildRail() {
      const tpl = this.querySelector('[data-rail-item-template]');
      if (!tpl || !this.rail) return;
      this.rail.style.setProperty('--step-count', String(this.steps.length));
      this.steps.forEach((step) => {
        const li = tpl.content.firstElementChild.cloneNode(true);
        li.dataset.railStep = step.id;
        li.querySelector('.routine-builder__rail-title').textContent = step.title;
        if (!step.required) {
          const small = document.createElement('small');
          small.textContent = ' (optional)';
          li.querySelector('.routine-builder__rail-title').appendChild(small);
        }
        li.tabIndex = 0;
        li.setAttribute('role', 'button');
        li.addEventListener('click', () => step.el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        li.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            step.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
        this.rail.appendChild(li);
        step.railEl = li;
      });
    }

    buildSlots() {
      const tpl = this.querySelector('[data-slot-template]');
      if (!tpl || !this.slots) return;
      this.steps.forEach((step) => {
        const li = tpl.content.firstElementChild.cloneNode(true);
        li.dataset.slot = step.id;
        li.querySelector('.routine-builder__slot-title').textContent = step.title;
        li.querySelector('.routine-builder__slot-product').textContent = step.emptyLabel;
        li.addEventListener('click', () => step.el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        this.slots.appendChild(li);
        step.slotEl = li;
      });
    }

    bind() {
      this.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-add-to-routine]');
        if (addBtn) return this.onAddClick(addBtn);

        const chip = e.target.closest('.routine-builder__chip');
        if (chip) return this.onFilter(chip);

        const preset = e.target.closest('[data-preset-variants]');
        if (preset) return this.applyPreset(preset.dataset.presetVariants);
      });

      this.addEventListener('change', (e) => {
        const select = e.target.closest('.routine-card__variant-select');
        if (select) this.onVariantChange(select);
      });

      if (this.submit) this.submit.addEventListener('click', () => this.addToCart());
    }

    /* ---------- card helpers ---------- */

    cardData(card) {
      let variants = [];
      try {
        variants = JSON.parse(card.querySelector('.routine-card__variants').textContent);
      } catch (err) {
        variants = [];
      }
      const select = card.querySelector('.routine-card__variant-select');
      let variant = null;
      if (select) {
        variant = variants.find((v) => String(v.id) === select.value) || null;
      }
      if (!variant) variant = variants.find((v) => v.available) || variants[0] || null;
      return { variants, variant, select };
    }

    stepFor(card) {
      return this.steps.find((s) => s.id === card.dataset.step);
    }

    /* ---------- interactions ---------- */

    onAddClick(button) {
      const card = button.closest('.routine-card');
      const step = this.stepFor(card);
      if (!step || card.dataset.available === 'false') return;

      if (step.selection && step.selection.card === card) {
        this.clearStep(step);
      } else {
        this.selectCard(step, card);
        this.scrollToNextIncomplete(step);
      }
      this.render();
    }

    onVariantChange(select) {
      const card = select.closest('.routine-card');
      const step = this.stepFor(card);
      if (step && step.selection && step.selection.card === card) {
        this.selectCard(step, card);
        this.render();
      }
    }

    selectCard(step, card) {
      const { variant } = this.cardData(card);
      if (!variant || !variant.available) return;
      if (step.selection && step.selection.card !== card) {
        step.selection.card.classList.remove('is-selected');
      }
      card.classList.add('is-selected');
      step.selection = {
        card,
        variantId: variant.id,
        price: Number(variant.price),
        title: card.dataset.productTitle,
        vendor: card.dataset.vendor,
        image: variant.image,
        variantTitle: variant.title,
      };
    }

    clearStep(step) {
      if (step.selection) step.selection.card.classList.remove('is-selected');
      step.selection = null;
    }

    onFilter(chip) {
      const group = chip.parentElement;
      const stepEl = chip.closest('.routine-builder__step');
      group.querySelectorAll('.routine-builder__chip').forEach((c) => c.classList.remove('is-active'));
      chip.classList.add('is-active');
      const filter = (chip.dataset.filter || '').toLowerCase();
      stepEl.querySelectorAll('.routine-card').forEach((card) => {
        const tags = (card.dataset.tags || '').toLowerCase();
        const show = !filter || tags.includes(filter);
        card.classList.toggle('is-hidden', !show);
      });
    }

    applyPreset(list) {
      const ids = String(list || '').split(',').filter(Boolean);
      this.steps.forEach((step) => this.clearStep(step));
      ids.forEach((id) => {
        this.steps.forEach((step) => {
          step.el.querySelectorAll('.routine-card').forEach((card) => {
            const { variants, select } = this.cardData(card);
            const match = variants.find((v) => String(v.id) === id);
            if (match) {
              if (select) select.value = String(match.id);
              this.selectCard(step, card);
            }
          });
        });
      });
      this.render();
      const first = this.steps.find((s) => !s.selection && s.required) || this.steps[0];
      if (first) first.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    scrollToNextIncomplete(fromStep) {
      const next = this.steps.find((s) => s.index > fromStep.index && !s.selection);
      if (next) {
        window.setTimeout(() => next.el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    }

    /* ---------- state ---------- */

    state() {
      const selected = this.steps.filter((s) => s.selection);
      const required = this.steps.filter((s) => s.required);
      const missingRequired = required.filter((s) => !s.selection);
      const optionalMissing = this.steps.filter((s) => !s.required && !s.selection);
      const count = selected.length;
      const subtotal = selected.reduce((sum, s) => sum + s.selection.price, 0);

      let percent = 0;
      if (missingRequired.length === 0) {
        if (count >= this.config.tier2Min) percent = this.config.tier2Pct;
        else if (count >= this.config.tier1Min) percent = this.config.tier1Pct;
      }
      const discount = Math.round((subtotal * percent) / 100);
      return { selected, required, missingRequired, optionalMissing, count, subtotal, percent, discount };
    }

    message(st) {
      const c = this.config;
      const fill = (tpl, vars) => tpl.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : ''));
      if (st.missingRequired.length) {
        const names = st.missingRequired.map((s) => s.title.toLowerCase());
        const step = names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names.slice(-1) : names[0];
        return fill(c.msgMissing, { step, percent: c.tier1Pct });
      }
      if (st.percent < c.tier2Pct && st.optionalMissing.length) {
        return fill(c.msgNext, { current: st.percent, step: st.optionalMissing[0].title.toLowerCase(), percent: c.tier2Pct });
      }
      return fill(c.msgMax, { percent: st.percent });
    }

    render() {
      const st = this.state();

      this.steps.forEach((step) => {
        const done = Boolean(step.selection);
        if (step.railEl) step.railEl.classList.toggle('is-complete', done);
        if (step.slotEl) {
          step.slotEl.classList.toggle('is-filled', done);
          const thumb = step.slotEl.querySelector('.routine-builder__slot-thumb');
          const name = step.slotEl.querySelector('.routine-builder__slot-product');
          if (done) {
            thumb.style.backgroundImage = step.selection.image ? `url("${step.selection.image}")` : '';
            name.textContent = step.selection.title;
          } else {
            thumb.style.backgroundImage = '';
            name.textContent = step.emptyLabel;
          }
        }
      });

      const active = this.steps.find((s) => !s.selection && s.required) || this.steps.find((s) => !s.selection);
      this.steps.forEach((s) => s.railEl && s.railEl.classList.toggle('is-active', s === active));

      if (this.progressBar) {
        const pct = Math.round((st.count / this.steps.length) * 100);
        this.progressBar.style.width = `${pct}%`;
      }
      if (this.tierMessage) {
        this.tierMessage.textContent = this.message(st);
        this.tierMessage.classList.toggle('is-unlocked', st.percent > 0);
      }

      if (this.totalEl) {
        if (st.count === 0) {
          this.totalEl.textContent = '—';
          this.compareEl.hidden = true;
        } else {
          this.totalEl.textContent = this.money(st.subtotal - st.discount);
          if (st.discount > 0) {
            this.compareEl.textContent = this.money(st.subtotal);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
      }

      if (this.submit) {
        this.submit.disabled = st.missingRequired.length > 0 || this.busy;
      }
    }

    money(cents) {
      const value = (cents / 100);
      const withDecimals = value.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const noDecimals = Math.round(value).toLocaleString('en-PK');
      return this.config.moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
    }

    /* ---------- cart ---------- */

    async addToCart() {
      const st = this.state();
      if (st.missingRequired.length || this.busy) return;

      const routineId = `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
      const items = st.selected.map((step) => ({
        id: step.selection.variantId,
        quantity: 1,
        properties: {
          _routine_id: routineId,
          _routine_step: step.key,
        },
      }));

      this.busy = true;
      this.setError('');
      this.submit.textContent = this.config.msgAdding;
      this.render();

      try {
        const response = await fetch(this.config.addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }

        if (this.config.afterAdd === 'cart') {
          window.location.href = this.config.cartUrl;
          return;
        }

        this.submit.textContent = this.config.msgAdded;
        this.setError('', `<a href="${this.config.cartUrl}">${this.config.viewCartLabel}</a>`);
        document.dispatchEvent(new CustomEvent('routine-builder:added', { detail: { items, routineId } }));
      } catch (err) {
        this.setError(this.config.msgError);
        this.submit.textContent = this.submitLabel;
      } finally {
        this.busy = false;
        this.render();
      }
    }

    setError(text, html) {
      if (!this.error) return;
      if (html) {
        this.error.innerHTML = html;
        this.error.classList.add('is-success');
        this.error.hidden = false;
      } else {
        this.error.textContent = text;
        this.error.classList.remove('is-success');
        this.error.hidden = !text;
      }
    }
  }

  if (!customElements.get('routine-builder')) {
    customElements.define('routine-builder', RoutineBuilder);
  }
})();